import react, { useEffect } from "react";
import { useState } from "react";
import { NavLink as RouterLink, useLocation } from "react-router-dom-v5-compat";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  ListItem,
  Tooltip,
} from "@mui/material";
import ChevronDownIcon from "../icons/ChevronDown";
import ChevronRightIcon from "../icons/ChevronRight";

const NavItem = (props) => {
  const {
    active,
    children,
    depth,
    icon,
    info,
    open: openProp,
    path,
    title,
    setExpanded,
    expanded,
    ...other
  } = props;
  const [open, setOpen] = useState(openProp);
  const location = useLocation();

  useEffect(() => {
    if (openProp) {
      setOpen(true);
    }
  }, [openProp]);

  const handleToggle = () => {
    setExpanded(true);
    setOpen((prevOpen) => !prevOpen);
  };

  const handleCollapsedParentClick = () => {
    setExpanded(true);
  };

  let paddingLeft = 16;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const isParentItem = Boolean(props.children);

  if (!expanded) {
    return (
      <ListItem
        disableGutters
        sx={{
          display: "flex",
          flexShrink: 0,
          justifyContent: "flex-start",
          py: 0,
          width: 48,
        }}
      >
        <Tooltip
          title={title}
          placement="right"
          disableFocusListener={expanded}
          disableHoverListener={expanded}
          disableInteractive={expanded}
          disableTouchListener={expanded}
        >
          <IconButton
            component={path && RouterLink}
            sx={{
              color: "text.secondary",
              fontWeight: "fontWeightMedium",
              height: 48,
              justifyContent: "center",
              padding: 0,
              textTransform: "none",
              width: 48,
              "& svg": {
                opacity: 0.65,
              },
              ...(active && {
                color: "primary.main",
                fontWeight: "fontWeightBold",
                "& svg": {
                  color: "primary.main",
                  opacity: 1,
                },
              }),
            }}
            variant="text"
            onClick={isParentItem ? handleCollapsedParentClick : undefined}
            to={!isParentItem ? path : location.pathname}
          >
            {icon}
          </IconButton>
        </Tooltip>
      </ListItem>
    );
  }

  return (
    <ListItem
      disableGutters
      sx={{
        display: "block",
        py: 0,
        whiteSpace: "initial",
      }}
      {...other}
    >
      <Button
        component={path && RouterLink}
        endIcon={
          isParentItem ? (
            !open ? (
              <ChevronRightIcon fontSize="small" />
            ) : (
              <ChevronDownIcon fontSize="small" />
            )
          ) : null
        }
        onClick={isParentItem && handleToggle}
        to={!isParentItem ? path : location.pathname}
        startIcon={icon}
        sx={{
          color: "text.secondary",
          fontWeight: "fontWeightMedium",
          height: 48,
          justifyContent: "flex-start",
          pl: depth === 0 ? 0 : `${paddingLeft}px`,
          pr: "8px",
          py: 0,
          textAlign: "left",
          textTransform: "none",
          width: "100%",
          "& .MuiButton-startIcon": {
            alignItems: "center",
            display: "inline-flex",
            flexShrink: 0,
            justifyContent: "center",
            ml: depth === 0 ? 0 : undefined,
            mr: depth === 0 ? 1.5 : undefined,
            minWidth: depth === 0 ? 48 : 20,
            width: depth === 0 ? 48 : 20,
          },
          "& .MuiButton-endIcon": {
            alignItems: "center",
            display: "inline-flex",
            flexShrink: 0,
            justifyContent: "center",
            minWidth: 20,
            width: 20,
          },
          "& svg": {
            opacity: 0.65,
          },
          ...(active && {
            color: "primary.main",
            fontWeight: "fontWeightBold",
            "& svg": {
              color: "primary.main",
              opacity: 1,
            },
          }),
        }}
        variant="text"
      >
        <Box
          sx={{
            flexGrow: 1,
            textOverflow: "elipsis",
            minWidth: isParentItem ? 175 : 200,
          }}
        >
          {title}
        </Box>
      </Button>
      {isParentItem && <Collapse in={open}>{children}</Collapse>}
    </ListItem>
  );
};

NavItem.propTypes = {
  active: PropTypes.bool,
  children: PropTypes.node,
  depth: PropTypes.number.isRequired,
  icon: PropTypes.node,
  info: PropTypes.node,
  open: PropTypes.bool,
  path: PropTypes.string,
  title: PropTypes.string.isRequired,
};

NavItem.defaultProps = {
  active: false,
  open: false,
};

export default NavItem;
